import { Box, Button, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslate } from "../../components/Translate/TranslateContext";

import FarmBasics from "./FarmBasics";
import GardenLocation from "./GardenLocation/GardenLocation";
import GardenHarvest from "./GardenHarvest";
import GardenIrrigation from "./GardenIrrigation";
import { getNearestWeatherStations } from "../../api/weatherstations";
import { createFarm } from "../../api/createFarm";

export default function FarmRegistration({
    user,
    cropTypes,
    cropVarieties,
    farm,
    setFarm
}) {
    const { translate } = useTranslate();


    const [step, setStep] = useState("farm");
    const [gardenIndex, setGardenIndex] = useState(0);
    const [weatherStations, setWeatherStations] = useState([]);
    const [geoRequested, setGeoRequested] = useState(false);

    const gardens = farm.gardens || [];
    const currentGarden = gardens[gardenIndex];

    useEffect(() => {
        if (
            step === "location" &&
            currentGarden &&
            !currentGarden.latitude_center &&
            !geoRequested &&
            navigator.geolocation
        ) {
            setGeoRequested(true);

            navigator.geolocation.getCurrentPosition(
                async (pos) => {
                    const lat = pos.coords.latitude;
                    const lon = pos.coords.longitude;

                    setFarm((prev) => ({
                        ...prev,
                        gardens: prev.gardens.map((g, i) =>
                            i === gardenIndex
                                ? { ...g, latitude_center: lat, longitude_center: lon }
                                : g
                        ),
                    }));

                    try {
                        const stations = await getNearestWeatherStations(lat, lon);
                        setWeatherStations(stations || []);
                    } catch {
                        setWeatherStations([]);
                    }
                },
                () => {}
            );
        }
    }, [step, currentGarden, gardenIndex, geoRequested]);

    useEffect(() => {
        if (
            step === "location" &&
            currentGarden?.latitude_center &&
            currentGarden?.longitude_center
        ) {
            const fetchStations = async () => {
                try {
                    const stations = await getNearestWeatherStations(
                        currentGarden.latitude_center,
                        currentGarden.longitude_center
                    );

                    setWeatherStations(stations || []);
                } catch {
                    setWeatherStations([]);
                }
            };

            fetchStations();
        }
    }, [step, currentGarden?.latitude_center, currentGarden?.longitude_center]);

    const submitFarm = async () => {
        try {
            const normalizedGardens = gardens.map((garden) => ({
                ...garden,
                preferred_ws_id:
                    garden.preferred_ws_id === undefined ||
                    garden.preferred_ws_id === null ||
                    garden.preferred_ws_id === ""
                        ? -1
                        : garden.preferred_ws_id,
            }));

            const res = await createFarm(
                farm.farm_name,
                normalizedGardens,
                user.user_id
            );

            if (res.success) {
                setFarm((prev) => ({
                    ...prev,
                    farm_id: res.farm_id,
                    farm_status: "data_setup",
                }));
            } else {
                console.error("Farm creation failed", res);
            }
        } catch (err) {
            console.error("Farm creation error", err);
        }
    };

    const nextGardenOrFinish = () => {
        if (gardenIndex < gardens.length - 1) {
            setGardenIndex(gardenIndex + 1);
            setStep("location");
        } else {
            setStep("preview");
        }
    };

    const updateGarden = (updatedGarden) => {
        setFarm((prev) => ({
            ...prev,
            gardens: prev.gardens.map((g, i) =>
                i === gardenIndex ? updatedGarden : g
            ),
        }));
    };

    const renderGardenStep = () => {
        if (!currentGarden) return null;

        if (step === "location")
            return (
                <GardenLocation
                    garden={currentGarden}
                    weatherStations={weatherStations}
                    onNext={() => setStep("harvest")}
                    setGarden={updateGarden}
                />
            );

        if (step === "harvest")
            return (
                <GardenHarvest
                    garden={currentGarden}
                    onNext={(harvestRows) => {
                        updateGarden({
                            ...currentGarden,
                            harvest: harvestRows,
                        });

                        setStep("irrigation");
                    }}
                />
            );

        if (step === "irrigation")
            return (
                <GardenIrrigation
                    garden={currentGarden}
                    onNext={(irrigationRows) => {
                        updateGarden({
                            ...currentGarden,
                            irrigation: irrigationRows,
                        });

                        nextGardenOrFinish();
                    }}
                />
            );

        return null;
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            {step === "farm" && (
                <FarmBasics
                    farmName={farm.farm_name}
                    setFarmName={(value) =>
                        setFarm((prev) => ({ ...prev, farm_name: value }))
                    }
                    gardens={gardens}
                    setGardens={(updated) =>
                        setFarm((prev) => ({ ...prev, gardens: updated }))
                    }
                    onNext={() => setStep("location")}
                    cropTypes={cropTypes}
                    cropVarieties={cropVarieties}
                />
            )}

            {step === "preview" && (
                <Box>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        {translate("farm_preview")}
                    </Typography>

                    <Typography sx={{ mb: 3 }}>
                        {translate("farm_name")}: {farm.farm_name}
                    </Typography>

                    {gardens.map((g, i) => (
                        <Box key={i} sx={{ mb: 3 }}>
                            <Typography variant="subtitle1">
                                {translate("garden")} {i + 1}: {g.garden_name}
                            </Typography>

                            <Typography variant="body2">
                                {translate("latitude")}: {g.latitude_center || ""}
                            </Typography>

                            <Typography variant="body2">
                                {translate("longitude")}: {g.longitude_center || ""}
                            </Typography>

                            <Typography variant="body2">
                                {translate("preferred_weather_station")}: {g.preferred_ws_id || "Open Meteo"}
                            </Typography>

                            <Typography variant="body2">
                                {translate("harvest_rows")}: {g.harvest?.length || 0}
                            </Typography>

                            <Typography variant="body2">
                                {translate("irrigation_rows")}: {g.irrigation?.length || 0}
                            </Typography>
                        </Box>
                    ))}

                    <Button variant="outlined" sx={{ mr: 2 }} onClick={() => setStep("farm")}>
                        {translate("edit_farm_and_gardens")}
                    </Button>

                    <Button variant="contained" onClick={submitFarm}>
                        {translate("continue")}
                    </Button>
                </Box>
            )}

            {step !== "farm" && step !== "preview" && (
                <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {translate("garden")} {gardenIndex + 1} / {gardens.length}: {currentGarden?.garden_name}
                    </Typography>

                    {renderGardenStep()}

                    <Button variant="text" sx={{ mt: 2 }} onClick={() => setStep("farm")}>
                        {translate("edit_farm_and_gardens")}
                    </Button>
                </>
            )}
        </Box>
    );
}
